import { useState, useEffect, useCallback, useMemo } from "react";
import { fetchParts, updatePart, fetchPmProductTypes, fetchSellableStock } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PencilSimple, CaretLeft, CaretRight, Spinner, CaretDown, CaretUp, SortAscending, SortDescending, Funnel, FilePdf, FileXls, X, Check, ToggleLeft, ToggleRight, File, ShoppingCartSimple, Plus, Wrench } from "@phosphor-icons/react";
import WavingFlag from "@/components/WavingFlag";
import PartFilesModal from "@/components/PartFilesModal";
import { exportPriceList } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useT } from "@/lib/i18n";
import { extractErr } from "@/lib/errors";
import { toast } from "sonner";

// Map table column keys (from DB) -> API field names for inline admin edit.
const EDITABLE_FIELD_MAP = {
  "List_Price": { field: "list_price", type: "number" },
  "List_Price_CA": { field: "list_price_ca", type: "number" },
  "Free Airdelivery_Max Working Pressure (CFM)": { field: "max_cfm", type: "text" },
  "Free Airdelivery_Min Working Pressure(CFM)": { field: "min_cfm", type: "text" },
  "Min Working Pressure(PSI)": { field: "min_psi", type: "text" },
  "Max Working Pressure(PSI)": { field: "max_psi", type: "text" },
  "Width": { field: "width", type: "number" },
  "Legth": { field: "length", type: "number" },
  "Height": { field: "height", type: "number" },
  "Weight": { field: "weight", type: "number" },
  "Explanation": { field: "explanation", type: "text" },
};

// Voltage limits
const VOLTAGE_LIMITS = {
  "220V 1Phz": "215V–230V", "220v 1Phz": "215V–230V",
  "220V 1-3Phz": "208V–230V", "220V 3Phz": "208V–230V", "220v 3Phz": "208V–230V",
  "208V-230V 3Phz": "208V–230V", "208V-230V 1-3Phz": "208V–230V",
  "380v 3Phz": "370V–415V", "380V 3Phz": "370V–415V",
  "480V 3Phz": "460V–505V", "575V 3Phz": "560V–615V",
};
function getVoltageLimits(spec) {
  if (!spec) return "-";
  if (VOLTAGE_LIMITS[spec]) return VOLTAGE_LIMITS[spec];
  const s = spec.toLowerCase();
  if (s.includes("575")) return "560V–615V";
  if (s.includes("480")) return "460V–505V";
  if (s.includes("380")) return "370V–415V";
  if (s.includes("220") || s.includes("208") || s.includes("230"))
    return s.includes("1phz") && !s.includes("3") ? "215V–230V" : "208V–230V";
  return "-";
}

// Column definitions with filter type.
//
// `hideForPartsOnly` (iter114) marks columns that are noise on the
// non-compressor price-list views (`stage='parts_only'` and the
// "Water/Oil Separators" tab). For those rows the user only cares
// about the Spec1 differentiator, the price, and the explanation.
//
// iter115 — Spec1 column heading is **category-aware**. Each tab
// supplies its own override label + matching i18n key so EN/FR/ES
// dictionaries can localize the heading per context. The fallback
// chain is: tab-specific label override → generic `Voltage` label.
const ALL_COLUMNS = [
  { key: "Display_Name", label: "Product Name", defW: 170, align: "left", sticky: true, filterType: "text" },
  // iter136.30 — Stock pill (US-CA) for compressor variants. Hidden
  // outside compressor categories via ``compressorOnly``, hidden from
  // customers/guests via ``hideForCustomer`` (applied at render time).
  { key: "Stock_US_CA", label: "Stock US-CA", defW: 100, align: "center", filterType: null,
    compressorOnly: true, hideForDryer: true, hideForFilter: true, hideForPartsOnly: true, isStockPill: true },
  { key: "Compressor_HP", label: "HP", defW: 55, align: "right", filterType: "range", numeric: true, hideForFilter: true, hideForPartsOnly: true },
  { key: "Spec1", label: "Voltage", defW: 120, align: "left", filterType: "select",
    // tab-specific heading overrides + i18n keys (iter115)
    dryerLabel: "Dew Point",                  dryerLabelKey: "table.col_spec1_dew_point",
    filterLabel: "Filtration Capacity",       filterLabelKey: "table.col_spec1_filtration_capacity",
    serviceKitsLabel: "Spec",                 serviceKitsLabelKey: "table.col_spec1_spec",
    waterOilSepLabel: "Max Compressor HP",    waterOilSepLabelKey: "table.col_spec1_max_compressor_hp",
    airTankLabel: "Size",                     airTankLabelKey: "table.col_spec1_size" },
  { key: "Voltage_Limits", label: "Min/Max Voltage", defW: 110, align: "left", filterType: null, hideForDryer: true, hideForFilter: true, hideForPartsOnly: true },
  { key: "List_Price", label: "List Price (USD)", defW: 120, align: "right", format: "usd", usOnly: true, filterType: "range", numeric: true },
  { key: "List_Price_CA", label: "List Price (CAD)", defW: 120, align: "right", format: "cad", filterType: "range", numeric: true },
  { key: "Free Airdelivery_Max Working Pressure (CFM)", label: "Max CFM", defW: 72, align: "right", filterType: "range", numeric: true, hideForPartsOnly: true },
  { key: "Free Airdelivery_Min Working Pressure(CFM)", label: "Min CFM", defW: 72, align: "right", filterType: "range", numeric: true, hideForPartsOnly: true },
  { key: "Min Working Pressure(PSI)", label: "Min PSI", defW: 68, align: "right", filterType: "range", numeric: true, hideForPartsOnly: true },
  { key: "Max Working Pressure(PSI)", label: "Max PSI", defW: 68, align: "right", filterType: "range", numeric: true, hideForPartsOnly: true },
  { key: "Width", label: "Width", defW: 62, align: "right", unit: '"', filterType: null },
  { key: "Legth", label: "Length", defW: 62, align: "right", unit: '"', filterType: null },
  { key: "Height", label: "Height", defW: 62, align: "right", unit: '"', filterType: null },
  { key: "Weight", label: "Weight", defW: 72, align: "right", unit: " lbs", filterType: null },
  { key: "Spec_3", label: "Connection Size", defW: 95, align: "left", filterType: "select", hideForPartsOnly: true },
  { key: "Noise dB(A)", label: "Noise dB(A)", defW: 78, align: "right", filterType: null, hideForFilter: true, hideForPartsOnly: true },
  { key: "Recommended_Dryer", label: "Rec. Dryer", defW: 130, align: "left", filterType: null,
    hideForDryer: true, hideForFilter: true, hideForTank: true, compressorOnly: true },
  { key: "Recommended_PRE_Filter", label: "Rec. PRE Filter", defW: 140, align: "left", filterType: null,
    hideForDryer: true, hideForFilter: true, hideForTank: true, compressorOnly: true },
  { key: "Recommended_POST_Filter", label: "Rec. POST Filter", defW: 140, align: "left", filterType: null,
    hideForDryer: true, hideForFilter: true, hideForTank: true, compressorOnly: true },
  { key: "Recommended_Tank", label: "Rec. Air Tank", defW: 110, align: "left", filterType: null,
    hideForDryer: true, hideForFilter: true, hideForTank: true, compressorOnly: true },
  { key: "Explanation", label: "Explanation", defW: 250, align: "left", filterType: "text" },
];

function formatCell(value, format, unit) {
  if (value == null || value === "" || value === "None") return "-";
  if (format === "usd") return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  if (format === "cad") return `C$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  if (unit && !isNaN(value) && value !== "") return `${value}${unit}`;
  if (typeof value === "number") return value.toLocaleString();
  const str = String(value);
  return str.length > 120 ? str.slice(0, 120) + "..." : str;
}

function useColumnWidths(columns) {
  const [widths, setWidths] = useState(() => {
    const m = {};
    columns.forEach((f) => { m[f.key] = f.defW; });
    return m;
  });
  const startResize = useCallback((key, startX) => {
    const startW = widths[key] || 100;
    const onMove = (e) => setWidths((prev) => ({ ...prev, [key]: Math.max(40, startW + (e.clientX - startX)) }));
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [widths]);
  return { widths, startResize };
}

const THEME = {
  US: {
    accent: "text-blue-700", accentBg: "bg-blue-50", accentBorder: "border-blue-100",
    accentHover: "hover:bg-blue-100/60", groupText: "text-blue-900", badge: "text-blue-500 bg-blue-100",
    sortHighlight: "text-blue-700 bg-blue-50", hoverRow: "hover:bg-blue-50/50",
    caretColor: "text-blue-600", headerBg: "bg-blue-50", hpColor: "text-blue-700",
    editBorder: "border-slate-200", editHover: "hover:bg-slate-100 hover:text-blue-700",
  },
  ACC: {
    accent: "text-[#D80621]", accentBg: "bg-red-50", accentBorder: "border-red-100",
    accentHover: "hover:bg-red-100/60", groupText: "text-red-900", badge: "text-red-600 bg-red-100",
    sortHighlight: "text-[#D80621] bg-red-50", hoverRow: "hover:bg-red-50/50",
    caretColor: "text-[#D80621]", headerBg: "bg-red-50", hpColor: "text-[#D80621]",
    editBorder: "border-red-200", editHover: "hover:bg-red-50 hover:text-[#D80621]",
  },
};

// Get cell value for filtering
function getCellValue(part, key) {
  if (key === "Compressor_HP") return part.Compressor_HP;
  if (key === "Voltage_Limits") return getVoltageLimits(part.Spec1);
  return part[key];
}

// Apply column filters to items
function applyFilters(items, filters, columns) {
  if (!items) return [];
  return items.filter((item) => {
    for (const col of columns) {
      const f = filters[col.key];
      if (!f) continue;
      const val = getCellValue(item, col.key);

      if (col.filterType === "range") {
        const num = val != null ? parseFloat(val) : NaN;
        if (f.min !== "" && !isNaN(parseFloat(f.min)) && (isNaN(num) || num < parseFloat(f.min))) return false;
        if (f.max !== "" && !isNaN(parseFloat(f.max)) && (isNaN(num) || num > parseFloat(f.max))) return false;
      } else if (col.filterType === "text") {
        if (f.value && val != null && !String(val).toLowerCase().includes(f.value.toLowerCase())) return false;
        if (f.value && val == null) return false;
      } else if (col.filterType === "select") {
        if (f.value && f.value !== "__all__") {
          if (String(val || "") !== f.value) return false;
        }
      }
    }
    return true;
  });
}

export default function PartsTable({ search, onEdit, onAddPart, canAddPart = false, refreshKey, groupMode = "class", prefix = "US", userEmail = "", isAdmin = false, user = null, productType = "", productTypes = "", nameContains = "", stage = "", onOpenPmSchedule }) {
  const tx = useT();
  // Helper: translate column label via t("table.col_<key>", default) with fallback to English label.
  // iter115 — when a column carries an explicit `i18nKey` (set per
  // category by the columns useMemo above), that key wins so the
  // category-specific Spec1 heading is correctly localized.
  const trLabel = (col) => {
    if (col.i18nKey) return tx(col.i18nKey, col.label);
    return tx(`table.col_${col.key.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`, col.label);
  };
  const isACC = prefix === "ACC";
  const theme = THEME[prefix] || THEME.US;
  // iter136.30 — Visibility flags for the Stock US-CA column.
  // Customers and guests should not see stock levels.
  const isCustomer = (user?.role || "").toLowerCase() === "customer";
  const isGuest = !user || !user.email;
  const isDryer = Boolean(productType);
  const isFilter = Boolean(productTypes);
  const isNonCompressor = isDryer || isFilter;
  // iter114 — Parts Price List + Water/Oil Separators are spec-list-
  // style: skip the by-class / by-HP buckets entirely AND default-sort
  // by Spec1 (the unique-enough differentiator). User can still click
  // any other column header to override; this only kicks in when
  // sortBy === ''.
  const isWaterOilSep = productType === "OIL/Water Separator";
  const isAirTank = productType === "Tank";
  const isServiceKitsOil = (stage || "").toLowerCase() === "parts_only";
  const isParts = isServiceKitsOil || isWaterOilSep;
  const columns = useMemo(() => ALL_COLUMNS.filter((c) => {
    if (isACC && c.usOnly) return false;
    if (isDryer && !isWaterOilSep && c.hideForDryer) return false;
    if (isFilter && c.hideForFilter) return false;
    // Recommended-accessory columns only make sense on the Compressor view.
    if (c.compressorOnly && isNonCompressor) return false;
    // In Parts Price List + Water/Oil Separators, the spec-only view
    // doesn't need voltage / HP / CFM / PSI / connection-size noise.
    if (isParts && c.hideForPartsOnly) return false;
    // iter136.30 — Stock column hidden from customers and guests.
    if (c.isStockPill && (isCustomer || isGuest)) return false;
    return true;
  }).map((c) => {
    // iter115 — apply category-specific Spec1 label override + matching
    // i18n key. Order matters: the most-specific tab wins.
    if (isWaterOilSep && c.waterOilSepLabel)
      return { ...c, label: c.waterOilSepLabel, i18nKey: c.waterOilSepLabelKey };
    if (isAirTank && c.airTankLabel)
      return { ...c, label: c.airTankLabel, i18nKey: c.airTankLabelKey };
    if (isServiceKitsOil && c.serviceKitsLabel)
      return { ...c, label: c.serviceKitsLabel, i18nKey: c.serviceKitsLabelKey };
    if (isFilter && c.filterLabel)
      return { ...c, label: c.filterLabel, i18nKey: c.filterLabelKey };
    if (isDryer && !isWaterOilSep && c.dryerLabel)
      return { ...c, label: c.dryerLabel, i18nKey: c.dryerLabelKey };
    return c;
  }),
  [isACC, isDryer, isFilter, isNonCompressor, isParts, isWaterOilSep, isAirTank, isServiceKitsOil]);

  // iter136.29 — Whether to render the wrench (PM Schedule) button at
  // all on this category's rows. Compressor / HE 2-stage / HEH 2-stage
  // tabs all qualify (= not a Dryer, Filter, or Parts-only category).
  // ``Product_CLass`` on the row is a numeric FK like "104" — not the
  // literal string "compressor" — so we can't infer this from the
  // row data.
  const showPmButton = !isNonCompressor && !isParts;
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage] = useState(200); // Load all to enable client filtering
  const [productClass, setProductClass] = useState("");
  const [loading, setLoading] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  // Admin-only: "in" shows only In-Price-List rows, "all" shows everything with toggle pills.
  const [scope, setScope] = useState("in");
  // iter136.30 — Admin-only "In stock only" toggle. Filters the
  // visible compressor rows down to those whose Display_Name has at
  // least one unit available in any active US/CA non-internal
  // location. Only meaningful on compressor tabs (where the stock
  // pill is rendered) and only available to non-customer/non-guest
  // viewers (same gate as the column itself).
  const [stockOnly, setStockOnly] = useState(false);
  const [filesPart, setFilesPart] = useState(null); // for PartFilesModal
  // iter136.29 — set of compressor `Product_Type`s that have at least
  // one PM-kit row registered. Used to render the wrench button as
  // "active" (clickable) vs "dimmed" (no PM Parts assigned for this
  // compressor) without one round-trip per row.
  const [pmTypes, setPmTypes] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchPmProductTypes()
      .then((d) => { if (!cancelled) setPmTypes(new Set(d?.types || [])); })
      .catch(() => { if (!cancelled) setPmTypes(new Set()); });
    return () => { cancelled = true; };
  }, []);
  // iter136.30 — sellable stock (US/CA) keyed by Parts.ID.
  // Fetched ONCE per page load when the viewer is allowed to see it
  // (= signed-in non-customer; the backend returns 403 for customers
  // and unauthenticated guests so we also gate at the call site to
  // avoid noisy 403s in the console).
  const canSeeStock = !isCustomer && !isGuest;
  const showStockColumn = canSeeStock && showPmButton; // same gate as PM button — compressor tabs only
  const [stockByPartId, setStockByPartId] = useState(null);
  useEffect(() => {
    if (!canSeeStock) { setStockByPartId({}); return; }
    let cancelled = false;
    fetchSellableStock()
      .then((d) => { if (!cancelled) setStockByPartId(d?.by_part_id || {}); })
      .catch(() => { if (!cancelled) setStockByPartId({}); });
    return () => { cancelled = true; };
  }, [canSeeStock]);
  const { widths, startResize } = useColumnWidths(columns);
  const { addItem } = useCart();

  const addToCart = useCallback((part) => {
    const isCAD = isACC;
    const price = isCAD ? part["List_Price_CA"] : part["List_Price"];
    if (price == null || price === "" || Number(price) <= 0) {
      toast.error("This item has no price set. Please contact sales for a quote.");
      return;
    }
    addItem({
      product_code: part["_ComputedKey"] || part["Product Name"] || String(part.ID),
      part_id: part.ID,
      product_name: part["Display_Name"] || part["Product Name"] || part["_ComputedKey"],
      price: Number(price) || 0,
      currency: isCAD ? "CAD" : "USD",
      hp: part["Compressor_HP"] ?? null,
      category: part["Product Type"] || part["Product_CLass"] || "",
    }, 1);
  }, [addItem, isACC]);

  const load = useCallback(() => {
    setLoading(true);
    fetchParts({
      page, per_page: perPage, search, product_class: productClass,
      in_price_list: (isAdmin && scope === "all") ? "" : "TRUE",
      sort_by: sortBy, sort_order: sortOrder,
      group_mode: groupMode, prefix,
      product_type: productType, product_types: productTypes, name_contains: nameContains,
      stage,
    }).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [page, perPage, search, productClass, sortBy, sortOrder, groupMode, prefix, productType, productTypes, nameContains, stage, isAdmin, scope]);

  useEffect(() => { setPage(1); setCollapsedGroups({}); }, [search, productClass, sortBy, sortOrder, prefix, groupMode, productType, productTypes, nameContains, stage, scope]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // Optimistically patch a single part after an admin inline save.
  const patchPartLocal = useCallback((partId, patch) => {
    setData((prev) => {
      if (!prev?.items) return prev;
      return { ...prev, items: prev.items.map((p) => p.ID === partId ? { ...p, ...patch } : p) };
    });
  }, []);

  // Save a single editable cell field for admins.
  const saveInlineField = useCallback(async (partId, columnKey, rawValue) => {
    const map = EDITABLE_FIELD_MAP[columnKey];
    if (!map) return;
    let value = rawValue;
    if (map.type === "number") {
      if (rawValue === "" || rawValue == null) return; // ignore blanks for numbers
      const num = parseFloat(rawValue);
      if (Number.isNaN(num)) return;
      value = num;
    }
    await updatePart(partId, { [map.field]: value });
    patchPartLocal(partId, { [columnKey]: value });
  }, [patchPartLocal]);

  // Toggle "In Price List" flag for a part.
  const toggleInPriceList = useCallback(async (part) => {
    const current = String(part["In Price List"] || "").toUpperCase() === "TRUE";
    const next = current ? "FALSE" : "TRUE";
    try {
      await updatePart(part.ID, { in_price_list: next });
      patchPartLocal(part.ID, { "In Price List": next });
    } catch (err) {
      toast.error(extractErr(err, "Failed to toggle").message);
    }
  }, [patchPartLocal]);

  const showScopeToggle = isAdmin;
  const showInListCol = isAdmin && scope === "all";

  // ─── iter114 — bulk Excel-style edit mode ────────────────────────
  // Active automatically when an admin has selected "all to show".
  // Every editable cell turns into a controlled <input>; the user can
  // tab through all of them (like an Excel worksheet) and a "Save N
  // changes" button at the top flushes everything in parallel.
  const bulkEditMode = isAdmin && scope === "all";
  // pendingEdits map, keyed `${partId}::${columnKey}` → string value.
  // Cleared after a successful save.
  const [pendingEdits, setPendingEdits] = useState(() => new Map());
  const [bulkSaving, setBulkSaving] = useState(false);
  const setBulkEdit = useCallback((partId, columnKey, value) => {
    setPendingEdits((prev) => {
      const next = new Map(prev);
      next.set(`${partId}::${columnKey}`, value);
      return next;
    });
  }, []);
  const dirtyCount = pendingEdits.size;
  const saveBulkEdits = useCallback(async () => {
    if (pendingEdits.size === 0) return;
    setBulkSaving(true);
    // Group edits by partId so we can fire one PUT per part.
    const byPart = new Map();
    for (const [k, raw] of pendingEdits.entries()) {
      const [partId, columnKey] = k.split("::");
      const map = EDITABLE_FIELD_MAP[columnKey];
      if (!map) continue;
      let value = raw;
      if (map.type === "number") {
        if (raw === "" || raw == null) continue;  // ignore blanks
        const num = parseFloat(raw);
        if (Number.isNaN(num)) continue;
        value = num;
      }
      if (!byPart.has(partId)) byPart.set(partId, { patch: {}, localPatch: {} });
      const entry = byPart.get(partId);
      entry.patch[map.field] = value;
      entry.localPatch[columnKey] = value;
    }
    let okCount = 0;
    let failCount = 0;
    const failed = [];
    await Promise.all(Array.from(byPart.entries()).map(async ([partId, { patch, localPatch }]) => {
      try {
        await updatePart(Number(partId), patch);
        patchPartLocal(Number(partId), localPatch);
        okCount += 1;
      } catch (err) {
        failCount += 1;
        failed.push({ partId, err: err?.response?.data?.detail || err?.message || "save failed" });
      }
    }));
    setBulkSaving(false);
    if (failCount === 0) {
      setPendingEdits(new Map());
      // toast inline (without sonner here to keep the dep small)
      // Use window.alert for failure path, success path is via the chip count clearing.
    } else {
      // Drop the saved entries, keep the failed ones so the user sees what's left.
      setPendingEdits((prev) => {
        const next = new Map(prev);
        for (const [k] of prev.entries()) {
          const [partId, columnKey] = k.split("::");
          const failedThis = failed.find((f) => String(f.partId) === String(partId));
          if (!failedThis) {
            // success — drop
            next.delete(k);
          }
        }
        return next;
      });
      // iter136.32 — bulk save summary; success on green, failures on red.
      if (failCount === 0) {
        toast.success(`Saved ${okCount} edits.`);
      } else {
        toast.error(`Saved ${okCount}, failed ${failCount}. First failure: ${failed[0]?.err || "?"}`);
      }
    }
  }, [pendingEdits, patchPartLocal]);
  const discardBulkEdits = useCallback(() => {
    setPendingEdits(new Map());
  }, []);
  // ─── iter115 — 2-click discard confirmation ──────────────────────
  // The previous implementation called `window.confirm`, which the
  // Codex preview iframe (and some Chromium configurations) silently
  // suppress. Result: admins clicked Discard and nothing happened.
  // Replaced with an inline 2-click pattern: first click arms the
  // button (label flips to "Click again to discard"), second click
  // commits. Auto-disarms after 4s if the user moves on.
  const [discardArmed, setDiscardArmed] = useState(false);
  useEffect(() => {
    if (!discardArmed) return undefined;
    const t = setTimeout(() => setDiscardArmed(false), 4000);
    return () => clearTimeout(t);
  }, [discardArmed]);
  const handleDiscardClick = useCallback(() => {
    if (pendingEdits.size === 0) return;
    if (!discardArmed) { setDiscardArmed(true); return; }
    discardBulkEdits();
    setDiscardArmed(false);
  }, [pendingEdits.size, discardArmed, discardBulkEdits]);

  // Get distinct values for select filters
  const selectOptions = useMemo(() => {
    if (!data?.items) return {};
    const opts = {};
    for (const col of columns) {
      if (col.filterType === "select") {
        const vals = new Set();
        for (const item of data.items) {
          const v = getCellValue(item, col.key);
          if (v != null && v !== "") vals.add(String(v));
        }
        opts[col.key] = Array.from(vals).sort();
      }
    }
    return opts;
  }, [data?.items, columns]);

  // Apply filters
  const filteredItems = useMemo(() => {
    let items = applyFilters(data?.items || [], filters, columns);
    // iter136.30 — Optional "in stock only" filter: drop rows whose
    // Display_Name has zero sellable inventory across active US/CA
    // locations. Only meaningful on compressor tabs (where
    // ``showStockColumn`` is true). When the rollup hasn't loaded yet
    // we keep all rows visible to avoid an empty flash.
    if (stockOnly && showStockColumn && stockByPartId) {
      items = items.filter((p) => {
        const s = stockByPartId[String(p.ID)];
        return Boolean(s && (Number(s.us || 0) > 0 || Number(s.ca || 0) > 0));
      });
    }
    return items;
  }, [data?.items, filters, columns, stockOnly, showStockColumn, stockByPartId]);

  // iter114 — Parts Price List + Water/Oil Separators are spec-list-
  // style: skip the by-class / by-HP buckets entirely AND default-sort
  // by Spec1 (the unique-enough differentiator). User can still click
  // any other column header to override; this only kicks in when
  // sortBy === ''.
  const partsItemsSorted = useMemo(() => {
    if (!isParts || sortBy) return filteredItems;
    const numeric = (v) => {
      const m = String(v ?? "").match(/-?\d+(?:\.\d+)?/);
      return m ? Number(m[0]) : Number.POSITIVE_INFINITY;
    };
    return [...filteredItems].sort((a, b) => {
      const aN = numeric(a.Spec1);
      const bN = numeric(b.Spec1);
      if (aN !== bN) return aN - bN;
      // tie-breaker → product name
      return String(a.Display_Name || "").localeCompare(String(b.Display_Name || ""));
    });
  }, [filteredItems, isParts, sortBy]);

  // Group filtered items
  const grouped = useMemo(() => {
    if (!filteredItems.length) return [];
    // iter114 — Service Kits / Oil view: 3 explicit buckets in fixed
    // order. Water/Oil Separators stays flat. User-set sort overrides.
    if (isServiceKitsOil) {
      if (sortBy) return [{ className: null, items: partsItemsSorted }];
      const buckets = {
        "Service Kits": [],
        "Oil / Lubricant": [],
        "Grease": [],
      };
      for (const item of partsItemsSorted) {
        const pt = (item["Product Type"] || "").trim();
        const pc = (item.Product_CLass || "").trim();
        if (pt === "Oil Filter Kit") buckets["Service Kits"].push(item);
        else if (pc === "Consumable: Grease") buckets["Grease"].push(item);
        else if (pt === "Oil") buckets["Oil / Lubricant"].push(item);
        else buckets["Service Kits"].push(item);  // safe default
      }
      // Drop empty buckets but keep the canonical order.
      return Object.entries(buckets)
        .filter(([, items]) => items.length > 0)
        .map(([className, items]) => ({ className, items }));
    }
    if (isParts) return [{ className: null, items: partsItemsSorted }];
    if (sortBy) return [{ className: null, items: filteredItems }];
    const map = new Map();
    for (const item of filteredItems) {
      const groupKey = groupMode === "hp"
        ? (item.Compressor_HP != null ? `${item.Compressor_HP} HP` : "Unknown HP")
        : (item.Resolved_Product_Class || item.Product_CLass || "Uncategorized");
      if (!map.has(groupKey)) map.set(groupKey, []);
      map.get(groupKey).push(item);
    }
    return Array.from(map.entries()).map(([className, items]) => ({ className, items }));
  }, [filteredItems, sortBy, groupMode, isParts, isServiceKitsOil, partsItemsSorted]);

  const toggleGroup = (className) => setCollapsedGroups((prev) => ({ ...prev, [className]: !prev[className] }));

  // Compute HP compatibility range per dryer SKU. Sort distinct HPs ascending; each row's
  // range goes from (previous distinct HP + 1) to (this HP). Smallest HP shows "up to X".
  const hpRangeByPartId = useMemo(() => {
    if (!isDryer || !filteredItems.length) return {};
    const hps = Array.from(new Set(filteredItems.map((p) => p.Compressor_HP).filter((h) => h != null))).sort((a, b) => a - b);
    const rangeMap = {};
    hps.forEach((hp, i) => {
      const prevMax = i === 0 ? null : hps[i - 1];
      rangeMap[hp] = prevMax == null ? `up to ${hp}` : (prevMax + 1 === hp ? `${hp}` : `${prevMax + 1}–${hp}`);
    });
    const out = {};
    for (const p of filteredItems) {
      if (p.Compressor_HP != null && rangeMap[p.Compressor_HP]) out[p.ID] = rangeMap[p.Compressor_HP];
    }
    return out;
  }, [isDryer, filteredItems]);
  const handleHeaderSort = (fieldKey) => {
    if (sortBy === fieldKey) {
      // cycle: asc → desc → off
      if (sortOrder === "asc") setSortOrder("desc");
      else { setSortBy(""); setSortOrder("asc"); }
    } else { setSortBy(fieldKey); setSortOrder("asc"); }
  };
  const clearSort = () => { setSortBy(""); setSortOrder("asc"); };

  const updateFilter = (key, type, field, value) => {
    setFilters((prev) => {
      const cur = prev[key] || (type === "range" ? { min: "", max: "" } : { value: "" });
      if (type === "range") return { ...prev, [key]: { ...cur, [field]: value } };
      return { ...prev, [key]: { value } };
    });
  };
  const clearFilters = () => setFilters({});
  const activeFilterCount = Object.values(filters).filter((f) =>
    f.value ? f.value !== "__all__" && f.value !== "" : (f.min !== "" || f.max !== "")
  ).length;

  if (!data && loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-slate-400" /></div>;
  }

  const total = data?.total || 0;
  const classes = data?.product_classes || [];
  // iter136.30 — Per Display_Name stock rollup. Sums stock from every
  // Parts row that shares the same Display_Name (variants like
  // "US15 1ph" / "US15 3ph" all roll up to "US15"). Calculated
  // client-side from the rendered rows so the totals naturally
  // ignore filtered-out rows.
  const stockByDisplayName = (() => {
    if (!showStockColumn || !stockByPartId) return null;
    const out = {};
    for (const p of (data?.items || [])) {
      const dn = p?.Display_Name;
      if (!dn) continue;
      const s = stockByPartId[String(p.ID)];
      if (!s) continue;
      if (!out[dn]) out[dn] = { us: 0, ca: 0 };
      out[dn].us += Number(s.us || 0);
      out[dn].ca += Number(s.ca || 0);
    }
    return out;
  })();
  // iter136.29 — Restrict the class dropdown to classes that actually
  // appear in the currently rendered rows, instead of every class in
  // the database. Keeps the user from seeing "Air Dryer" classes on
  // the Compressor tab. When a specific class is already selected,
  // ``data.items`` only contains rows of that one class (server-side
  // filter), so we add the currently-selected class back into the
  // visible set so users can switch to another via the same dropdown
  // without first hitting "All Classes".
  const visibleClasses = (() => {
    const present = new Set();
    for (const p of (data?.items || [])) {
      if (p?.Product_CLass) present.add(p.Product_CLass);
    }
    if (productClass) present.add(productClass);
    return classes.filter((c) => present.has(c.key));
  })();
  // Actions column is always rendered. Width = edit(optional) + cart + files.
  const hasActionsCol = true;
  const actionsColWidth = onEdit ? 96 : 68;
  const extraLeftCols = (hasActionsCol ? 1 : 0) + (showInListCol ? 1 : 0);
  const colCount = columns.length + extraLeftCols;
  // Tailwind can't scan template-literal classes, so we map the two widths to static classes.
  const stickyLeftEdit = onEdit ? "left-[96px]" : "left-[68px]";

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <span data-testid="parts-total-count" className={`text-xs font-bold tracking-[0.2em] whitespace-nowrap ${theme.accent}`}>
            <span className="uppercase">{filteredItems.length} / {total}</span>{" "}
            {isACC ? (
              <><b className="text-[#D80621]">A</b><span className="opacity-40">ir</span> <b className="text-[#D80621]">C</b><span className="opacity-40">ompressor</span> <b className="text-[#D80621]">C</b><span className="opacity-40">anada</span></>
            ) : (
              <>US Air Compressor</>
            )}
          </span>
          {/* Waving Flag - canvas based cloth simulation */}
          <div data-testid="country-flag" className="flex items-center">
            {isACC ? (
              <WavingFlag src="https://flagcdn.com/w320/ca.png" width={90} height={56} speed={0.8} />
            ) : (
              <WavingFlag src="https://flagcdn.com/w320/us.png" width={90} height={56} speed={0.8} />
            )}
          </div>
          <Select value={productClass} onValueChange={(v) => setProductClass(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="parts-class-filter" className="w-[280px] h-8 rounded-sm text-sm border-slate-300">
              <SelectValue placeholder="All Classes" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              <SelectItem value="all">All Classes</SelectItem>
              {visibleClasses.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <button data-testid="toggle-filters-button" onClick={() => setShowFilters(!showFilters)}
            className={`text-xs font-semibold px-2.5 py-1.5 rounded-sm flex items-center gap-1.5 border transition-colors ${
              showFilters || activeFilterCount > 0
                ? (isACC ? "text-[#D80621] bg-red-50 border-red-200" : "text-blue-700 bg-blue-50 border-blue-200")
                : "text-slate-500 bg-white border-slate-200 hover:bg-slate-50"
            }`}>
            <Funnel size={13} weight={activeFilterCount > 0 ? "fill" : "bold"} />
            Filters{activeFilterCount > 0 && ` (${activeFilterCount})`}
          </button>
          {/* iter136.30 — "In stock only" toggle. Compressor tabs only,
              only for non-customer/non-guest viewers (= same gate as the
              Stock US-CA column). Sits next to Filters because it's a
              row-level filter, not a scope-level switch. */}
          {showStockColumn && (
            <button
              data-testid="parts-instock-only-toggle"
              onClick={() => setStockOnly(!stockOnly)}
              title={stockOnly ? "Showing only compressors with sellable stock" : "Click to hide compressors with no available inventory"}
              className={`text-xs font-semibold px-2.5 py-1.5 rounded-sm flex items-center gap-1.5 border transition-colors ${
                stockOnly
                  ? "text-emerald-800 bg-emerald-50 border-emerald-300"
                  : "text-slate-500 bg-white border-slate-200 hover:bg-slate-50"
              }`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${stockOnly ? "bg-emerald-600" : "bg-slate-300"}`} />
              In stock only
            </button>
          )}
          {showScopeToggle && (
            <div data-testid="scope-toggle" className="inline-flex items-center rounded-sm border border-slate-200 overflow-hidden text-xs font-semibold">
              <button data-testid="scope-toggle-in"
                onClick={() => setScope("in")}
                className={`px-2.5 py-1.5 transition-colors ${
                  scope === "in"
                    ? (isACC ? "bg-red-50 text-[#D80621]" : "bg-blue-50 text-blue-700")
                    : "bg-white text-slate-500 hover:bg-slate-50"
                }`}>
                In Price List
              </button>
              <button data-testid="scope-toggle-all"
                onClick={() => setScope("all")}
                className={`px-2.5 py-1.5 border-l border-slate-200 transition-colors ${
                  scope === "all"
                    ? (isACC ? "bg-red-50 text-[#D80621]" : "bg-blue-50 text-blue-700")
                    : "bg-white text-slate-500 hover:bg-slate-50"
                }`}>
                All
              </button>
            </div>
          )}
          {/* iter114 — Excel-mode save bar. Visible only when admin
              + scope=all (= bulkEditMode). Hidden until at least one
              cell is dirty so the toolbar isn't visually noisy. */}
          {bulkEditMode && dirtyCount > 0 && (
            <div data-testid="bulk-edit-bar" className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-amber-300 bg-amber-50 text-xs">
              <span className="font-bold text-amber-900">
                {tx("parts_table.unsaved_changes", "{count} unsaved changes", { count: dirtyCount })
                  .replace("{count}", String(dirtyCount))}
              </span>
              <button
                onClick={saveBulkEdits}
                disabled={bulkSaving}
                data-testid="bulk-edit-save"
                className="px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50">
                {bulkSaving ? tx("parts_table.saving", "Saving…") : tx("parts_table.save_changes", "Save changes")}
              </button>
              <button
                onClick={handleDiscardClick}
                disabled={bulkSaving}
                data-testid="bulk-edit-discard"
                title={discardArmed
                  ? tx("parts_table.discard_armed_hint", "Click again to confirm — this will revert all unsaved changes")
                  : tx("parts_table.discard_hint", "Discard all unsaved bulk edits")}
                className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors disabled:opacity-50 ${
                  discardArmed
                    ? "bg-red-600 text-white border-red-700 hover:bg-red-700"
                    : "border-amber-400 text-amber-800 hover:bg-amber-100"
                }`}>
                {discardArmed
                  ? tx("parts_table.discard_armed", "Click again to discard")
                  : tx("parts_table.discard", "Discard")}
              </button>
            </div>
          )}
          {bulkEditMode && dirtyCount === 0 && (
            <span data-testid="bulk-edit-hint" className="text-[10px] text-slate-400 italic">
              {tx("parts_table.bulk_edit_hint", "Excel-mode active — type to edit any cell, then click Save.")}
            </span>
          )}
          {activeFilterCount > 0 && (
            <button onClick={clearFilters}
              className="text-xs text-slate-500 hover:text-slate-700 underline">Clear all</button>
          )}
          {sortBy && (
            <button data-testid="clear-sort-button" onClick={clearSort}
              className={`text-xs font-semibold px-2 py-1 rounded-sm flex items-center gap-1 border ${
                isACC ? "text-[#D80621] bg-red-50 border-red-200" : "text-blue-700 bg-blue-50 border-blue-200"
              }`}>
              {sortOrder === "asc" ? <SortAscending size={13} /> : <SortDescending size={13} />}
              {columns.find(f => f.key === sortBy)?.label || sortBy}
              <span className="ml-1 opacity-50">&times;</span>
            </button>
          )}
        </div>
        {/* Download buttons */}
        <div className="flex items-center gap-2">
          <DownloadButton format="excel" prefix={prefix} productClass={productClass} search={search}
            filters={filters} userEmail={userEmail} isACC={isACC} productType={productType} productTypes={productTypes} nameContains={nameContains} stage={stage} />
          <DownloadButton format="pdf" prefix={prefix} productClass={productClass} search={search}
            filters={filters} userEmail={userEmail} isACC={isACC} productType={productType} productTypes={productTypes} nameContains={nameContains} stage={stage} />
        </div>
      </div>

      <p className="text-xs text-slate-400 mb-2">Click a column header to sort. Drag edges to resize.{isAdmin ? " Double-click a price/spec cell to edit it inline." : ""}</p>

      <div className="border border-slate-200 rounded-sm overflow-x-auto relative"
        style={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}>
        <table data-testid="parts-table" className="data-table border-collapse" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: `${actionsColWidth}px` }} />
            {showInListCol && <col style={{ width: "70px" }} />}
            {columns.map((f) => <col key={f.key} style={{ width: `${widths[f.key]}px` }} />)}
          </colgroup>
          <thead className="sticky top-0 z-30">
            {/* Header row */}
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="px-1 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 text-center bg-slate-50 sticky left-0 z-40 border-r-2 border-r-slate-300" style={{width: actionsColWidth}}>{onEdit ? tx("table.actions", "Actions") : tx("table.files", "Files")}</th>
              {showInListCol && <th className="px-1 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 text-center bg-slate-50 border-r-2 border-r-slate-200" style={{width: 70}}>{tx("table.in_list", "In List")}</th>}
              {columns.map((f) => (
                <th key={f.key}
                  className={`relative px-2 py-2 text-xs font-bold uppercase tracking-[0.1em] whitespace-nowrap select-none border-r border-slate-100 ${
                    f.align === "right" ? "text-right" : "text-left"
                  } ${sortBy === f.key ? theme.sortHighlight : "text-slate-500 bg-slate-50"} ${
                    f.sticky ? `sticky ${stickyLeftEdit} z-40 border-r-2 border-r-slate-300 bg-slate-50 md:shadow-none shadow-[4px_0_6px_-4px_rgba(15,23,42,0.25)]` : ""}`}
                  onClick={() => handleHeaderSort(f.key)} style={{ cursor: "pointer" }}
                  data-testid={`sort-${f.key}`}
                  data-sort-active={sortBy === f.key ? sortOrder : "off"}>
                  <span className="flex items-center gap-1">
                    {trLabel(f)}
                    {sortBy === f.key && (sortOrder === "asc" ? <SortAscending size={12} weight="bold" /> : <SortDescending size={12} weight="bold" />)}
                  </span>
                  <div className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500"
                    onMouseDown={(e) => { e.preventDefault(); startResize(f.key, e.clientX); }} />
                </th>
              ))}
            </tr>
            {/* Filter row */}
            {showFilters && (
              <tr className="bg-slate-100 border-b border-slate-200">
                <td className="px-1 py-1 bg-slate-100 sticky left-0 z-40 border-r-2 border-r-slate-300" />
                {showInListCol && <td className="px-1 py-1 bg-slate-100 border-r-2 border-r-slate-200" />}
                {columns.map((f) => (
                  <td key={f.key}
                    className={`px-1 py-1 ${
                      f.sticky ? `sticky ${stickyLeftEdit} z-40 border-r-2 border-r-slate-300 bg-slate-100 md:shadow-none shadow-[4px_0_6px_-4px_rgba(15,23,42,0.25)]` : ""}`}>
                    <FilterCell
                      col={f}
                      value={filters[f.key]}
                      options={selectOptions[f.key]}
                      onChange={(field, val) => updateFilter(f.key, f.filterType, field, val)}
                    />
                  </td>
                ))}
              </tr>
            )}
          </thead>
          <tbody>
            {loading && grouped.length === 0 ? (
              <tr><td colSpan={colCount} className="text-center py-12 text-slate-400">Loading...</td></tr>
            ) : grouped.length === 0 ? (
              <tr><td colSpan={colCount} className="text-center py-12 text-slate-400">No compressors match filters</td></tr>
            ) : (
              grouped.map(({ className, items }) =>
                className === null
                  ? items.map((part, idx) => <DataRow key={part.ID} part={part} onEdit={onEdit} rowIdx={idx} columns={columns} theme={theme} isACC={isACC} stickyLeftEdit={stickyLeftEdit} isDryer={isDryer} hpRange={hpRangeByPartId[part.ID]} isAdmin={isAdmin} showInListCol={showInListCol} onToggleInList={toggleInPriceList} onSaveField={saveInlineField} onViewFiles={setFilesPart} onAddToCart={addToCart} onOpenPmSchedule={onOpenPmSchedule} pmTypes={pmTypes} showPmButton={showPmButton} stockByDisplayName={stockByDisplayName} actionsColWidth={actionsColWidth}
                      bulkEditMode={bulkEditMode} bulkEdits={pendingEdits} onBulkEdit={setBulkEdit} />)
                  : <GroupRows key={className} groupName={className} items={items}
                      isCollapsed={collapsedGroups[className]} onToggle={() => toggleGroup(className)}
                      onEdit={onEdit} colCount={colCount} columns={columns} theme={theme} isACC={isACC} stickyLeftEdit={stickyLeftEdit}
                      isDryer={isDryer} hpRangeByPartId={hpRangeByPartId}
                      isAdmin={isAdmin} showInListCol={showInListCol} onToggleInList={toggleInPriceList} onSaveField={saveInlineField}
                      onViewFiles={setFilesPart} onAddToCart={addToCart} onOpenPmSchedule={onOpenPmSchedule} pmTypes={pmTypes} showPmButton={showPmButton} stockByDisplayName={stockByDisplayName} actionsColWidth={actionsColWidth}
                      bulkEditMode={bulkEditMode} bulkEdits={pendingEdits} onBulkEdit={setBulkEdit}
                      canAddPart={canAddPart} onAddPart={onAddPart} />
              )
            )}
          </tbody>
        </table>
      </div>
      {filesPart && <PartFilesModal part={filesPart} onClose={() => setFilesPart(null)} audience={isAdmin ? "all" : "customer"} />}
    </div>
  );
}

function FilterCell({ col, value, options, onChange }) {
  if (!col.filterType) return null;

  if (col.filterType === "range") {
    const v = value || { min: "", max: "" };
    return (
      <div className="flex gap-0.5">
        <input data-testid={`filter-${col.key}-min`} type="number" placeholder="Min"
          value={v.min} onChange={(e) => onChange("min", e.target.value)}
          className="w-full min-w-0 h-6 text-[10px] px-1 border border-slate-300 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500" />
        <input data-testid={`filter-${col.key}-max`} type="number" placeholder="Max"
          value={v.max} onChange={(e) => onChange("max", e.target.value)}
          className="w-full min-w-0 h-6 text-[10px] px-1 border border-slate-300 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500" />
      </div>
    );
  }

  if (col.filterType === "select" && options) {
    const v = value?.value || "__all__";
    return (
      <select data-testid={`filter-${col.key}`} value={v}
        onChange={(e) => onChange("value", e.target.value)}
        className="w-full h-6 text-[10px] px-1 border border-slate-300 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 truncate">
        <option value="__all__">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  // text
  const v = value?.value || "";
  return (
    <input data-testid={`filter-${col.key}`} type="text" placeholder="Filter..."
      value={v} onChange={(e) => onChange("value", e.target.value)}
      className="w-full h-6 text-[10px] px-1 border border-slate-300 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500" />
  );
}

function InlineEditCell({ part, columnKey, display, onSave, align, className,
                          bulkEditMode = false, bulkEdits, onBulkEdit }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);

  // ─── iter114 — bulk Excel-style mode ──────────────────────────
  // Always-on input wired to the parent's `pendingEdits` map. No
  // per-cell save call; user hits the toolbar "Save N changes" button
  // when ready.
  if (bulkEditMode) {
    const dirtyKey = `${part.ID}::${columnKey}`;
    const isDirty = bulkEdits?.has(dirtyKey);
    const baseValue = part[columnKey] == null || part[columnKey] === "None"
      ? "" : String(part[columnKey]);
    const v = isDirty ? bulkEdits.get(dirtyKey) : baseValue;
    return (
      <input
        type="text"
        value={v}
        onChange={(e) => onBulkEdit(part.ID, columnKey, e.target.value)}
        data-testid={`bulk-cell-${part.ID}-${columnKey}`}
        className={`w-full h-6 text-[11px] px-1 border rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500 ${isDirty ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"} ${align === "right" ? "text-right" : ""} ${className || ""}`}
      />
    );
  }

  const startEdit = (e) => {
    e.stopPropagation();
    const raw = part[columnKey];
    setValue(raw == null || raw === "None" ? "" : String(raw));
    setEditing(true);
  };
  const cancel = () => { setEditing(false); };
  const commit = async () => {
    setSaving(true);
    try {
      await onSave(part.ID, columnKey, value);
      setEditing(false);
    } catch (err) {
      toast.error(extractErr(err, "Failed to save").message);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div onDoubleClick={startEdit}
        data-testid={`inline-cell-${part.ID}-${columnKey}`}
        className={`cursor-text hover:ring-1 hover:ring-blue-300 rounded-sm px-1 -mx-1 ${align === "right" ? "text-right" : ""}`}
        title="Double-click to edit">
        {display}
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
      <input autoFocus value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") cancel();
        }}
        onBlur={commit}
        disabled={saving}
        data-testid={`inline-input-${part.ID}-${columnKey}`}
        className={`w-full min-w-0 h-6 text-xs px-1 border border-blue-400 rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 ${align === "right" ? "text-right font-mono" : ""}`} />
    </div>
  );
}

function DataRow({ part, onEdit, rowIdx, columns, theme, isACC, stickyLeftEdit, isDryer, hpRange, isAdmin, showInListCol, onToggleInList, onSaveField, onViewFiles, onAddToCart, onOpenPmSchedule, pmTypes, showPmButton, stockByDisplayName, actionsColWidth,
                   bulkEditMode, bulkEdits, onBulkEdit }) {
  const voltageLimits = getVoltageLimits(part.Spec1);
  const isOdd = rowIdx % 2 === 1;
  const oddBg = isACC ? "bg-red-50" : "bg-slate-50";
  const oddSticky = isACC ? "bg-red-50" : "bg-slate-50";
  const inPriceList = String(part["In Price List"] || "").toUpperCase() === "TRUE";
  const dimCls = inPriceList ? "" : "text-slate-400 opacity-60";
  const priceKey = isACC ? "List_Price_CA" : "List_Price";
  const hasPrice = part[priceKey] != null && part[priceKey] !== "" && Number(part[priceKey]) > 0;

  return (
    <tr data-testid={`parts-table-row-${part.ID}`}
      data-in-price-list={inPriceList ? "true" : "false"}
      className={`border-b border-slate-100 transition-colors ${isOdd ? oddBg : "bg-white"} ${theme.hoverRow} ${dimCls}`}>
      <td className={`px-1 py-1.5 text-center sticky left-0 z-20 border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"}`} style={{width: actionsColWidth}}>
        <div className="flex items-center justify-center gap-1">
          {onEdit && (
            <button data-testid={`parts-table-row-edit-button-${part.ID}`}
              className={`inline-flex items-center justify-center w-6 h-6 rounded-sm border transition-colors text-slate-500 ${theme.editBorder} ${theme.editHover}`}
              onClick={() => onEdit(part)} title="Edit part">
              <PencilSimple size={13} />
            </button>
          )}
          <button data-testid={`parts-table-row-cart-button-${part.ID}`}
            className={`inline-flex items-center justify-center w-6 h-6 rounded-sm border transition-colors ${
              hasPrice
                ? "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                : "border-slate-200 text-slate-300 cursor-not-allowed"
            }`}
            disabled={!hasPrice}
            onClick={() => hasPrice && onAddToCart?.(part)}
            title={hasPrice ? "Add to cart" : "No price available"}>
            <ShoppingCartSimple size={13} weight="bold" />
          </button>
          <button data-testid={`parts-table-row-files-button-${part.ID}`}
            className={`inline-flex items-center justify-center w-6 h-6 rounded-sm border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-blue-700 transition-colors`}
            onClick={() => onViewFiles?.(part)} title="View related files">
            <File size={13} />
          </button>
          {onOpenPmSchedule && showPmButton && (() => {
            // iter136.29 — render the wrench on EVERY compressor-tab row.
            // If pmTypes is still loading we pessimistically show the
            // active state so we don't flash-dim then re-enable; once
            // the set arrives, missing entries flip to the dimmed
            // "no PM Parts assigned" indicator.
            const pmKey = String(part._ComputedKey || part["_ComputedKey"] || "").trim();
            const isLoaded = pmTypes !== null && pmTypes !== undefined;
            const hasPm = !isLoaded || pmTypes.has(pmKey);
            return hasPm ? (
              <button data-testid={`parts-table-row-pm-button-${part.ID}`}
                className="inline-flex items-center justify-center w-6 h-6 rounded-sm border border-amber-200 text-amber-700 hover:bg-amber-50 transition-colors"
                onClick={() => onOpenPmSchedule(part)} title="PM Schedule">
                <Wrench size={13} weight="bold" />
              </button>
            ) : (
              <span data-testid={`parts-table-row-pm-button-disabled-${part.ID}`}
                className="inline-flex items-center justify-center w-6 h-6 rounded-sm border border-slate-200 bg-slate-50 text-slate-300 cursor-not-allowed"
                title="No PM Parts assigned for this compressor"
                aria-disabled="true">
                <Wrench size={13} weight="bold" />
              </span>
            );
          })()}
        </div>
      </td>
      {showInListCol && (
        <td className={`px-1 py-1.5 text-center border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"}`}>
          <button data-testid={`parts-table-inlist-toggle-${part.ID}`}
            onClick={() => onToggleInList(part)}
            title={inPriceList ? "Remove from Price List" : "Add to Price List"}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm border text-[10px] font-bold uppercase tracking-wider transition-colors ${
              inPriceList
                ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
                : "bg-slate-100 text-slate-400 border-slate-200 hover:bg-slate-200"
            }`}>
            {inPriceList ? <ToggleRight size={13} weight="fill" /> : <ToggleLeft size={13} />}
            {inPriceList ? "ON" : "OFF"}
          </button>
        </td>
      )}
      {columns.map((f) => {
        // iter136.30 — Stock US-CA pill cell. Renders only when both
        // (a) the column is enabled (gated server-side from
        // customers/guests) AND (b) the parent passed a Display_Name
        // -> {us, ca} rollup.
        if (f.isStockPill) {
          const dn = part?.Display_Name;
          const agg = (stockByDisplayName && dn && stockByDisplayName[dn]) || null;
          const us = agg ? Math.round(agg.us) : 0;
          const ca = agg ? Math.round(agg.ca) : 0;
          const has = us > 0 || ca > 0;
          return (
            <td key={f.key}
              className={`px-2 py-1.5 text-sm text-center border-r border-slate-50 ${isOdd ? "" : ""}`}
              data-testid={`stock-pill-${part.ID}`}
              title={has ? `${us} units in US warehouses, ${ca} units in Canada warehouses (excludes Repair / Return / Demolish / Decompose / Production / Used / Conversion locations)` : "No sellable inventory in active US/CA locations for this Display Name."}>
              {has ? (
                <span className="inline-flex items-center gap-0.5 text-[11px] font-mono font-bold">
                  <span className={us > 0 ? "text-blue-700" : "text-slate-300"}>{us}</span>
                  <span className="text-slate-400">-</span>
                  <span className={ca > 0 ? "text-red-700" : "text-slate-300"}>{ca}</span>
                </span>
              ) : (
                <span className="text-slate-300 text-sm">—</span>
              )}
            </td>
          );
        }
        let cellValue;
        if (f.key === "Compressor_HP") cellValue = part.Compressor_HP != null ? `${part.Compressor_HP} HP` : "-";
        else if (f.key === "Voltage_Limits") cellValue = voltageLimits;
        else cellValue = formatCell(part[f.key], f.format, f.unit);

        const isName = f.key === "Display_Name";
        const editable = isAdmin && EDITABLE_FIELD_MAP[f.key];
        const isRec = f.compressorOnly;
        const recVal = isRec ? (part[f.key] || "") : "";
        const recIncluded = isRec && recVal === "Included";
        const recSep = isRec && /separately/i.test(recVal);
        return (
          <td key={f.key}
            className={`px-2 py-1.5 text-sm overflow-hidden text-ellipsis whitespace-nowrap border-r border-slate-50 ${
              f.align === "right" ? "text-right font-mono" : "text-left"
            } ${isName ? "font-semibold text-slate-900" : ""} ${
              f.key === "Compressor_HP" ? `font-bold ${theme.hpColor}` : ""
            } ${(f.format === "usd" || f.format === "cad") ? "font-semibold text-slate-900" : "text-slate-700"} ${
              f.sticky ? `sticky ${stickyLeftEdit} z-20 border-r-2 border-r-slate-200 ${isOdd ? oddSticky : "bg-white"} md:shadow-none shadow-[4px_0_6px_-4px_rgba(15,23,42,0.25)]` : ""}`}
            title={f.key === "Voltage_Limits" ? voltageLimits : (part[f.key] != null ? String(part[f.key]) : "")}>
            {isName ? (
              <div className="flex flex-col overflow-hidden">
                <span className="truncate">{cellValue}</span>
                {isDryer && hpRange && (
                  <span className="text-[9px] font-normal text-slate-500 truncate" data-testid={`hp-compat-${part.ID}`}>
                    Compatible with {hpRange} HP compressors
                  </span>
                )}
              </div>
            ) : isRec ? (
              recIncluded ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200"
                      data-testid={`rec-included-${part.ID}-${f.key}`}>Included</span>
              ) : recSep ? (
                <span className="text-[10px] text-slate-400 italic">Sold separately</span>
              ) : recVal && recVal !== "-" ? (
                <span className="inline-block text-[11px] font-semibold text-blue-800"
                      data-testid={`rec-part-${part.ID}-${f.key}`}>{recVal}</span>
              ) : <span className="text-slate-300">–</span>
            ) : editable ? (
              <InlineEditCell part={part} columnKey={f.key} display={cellValue} align={f.align} onSave={onSaveField}
                bulkEditMode={bulkEditMode} bulkEdits={bulkEdits} onBulkEdit={onBulkEdit} />
            ) : cellValue}
          </td>
        );
      })}
    </tr>
  );
}

function GroupRows({ groupName, items, isCollapsed, onToggle, onEdit, colCount, columns, theme, isACC, stickyLeftEdit, isDryer, hpRangeByPartId, isAdmin, showInListCol, onToggleInList, onSaveField, onViewFiles, onAddToCart, onOpenPmSchedule, pmTypes, showPmButton, stockByDisplayName, actionsColWidth, onAddPart, canAddPart,
                    bulkEditMode, bulkEdits, onBulkEdit }) {
  return (
    <>
      <tr data-testid={`parts-group-header-${groupName}`}
        className={`border-b cursor-pointer transition-colors ${theme.accentBg} ${theme.accentBorder} ${theme.accentHover}`}
        onClick={onToggle}>
        <td colSpan={colCount} className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            {isCollapsed
              ? <CaretDown size={14} weight="bold" className={theme.caretColor} />
              : <CaretUp size={14} weight="bold" className={theme.caretColor} />}
            <span className={`text-sm font-bold font-['Chivo'] tracking-tight ${theme.groupText}`}>{groupName}</span>
            <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-sm ${theme.badge}`}>{items.length}</span>
            {canAddPart && (
              <button data-testid={`parts-group-add-${groupName}`}
                onClick={(e) => { e.stopPropagation(); onAddPart?.(groupName, items); }}
                className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-white text-blue-700 border border-blue-200 px-2 py-1 rounded-sm hover:bg-blue-50 transition-colors">
                <Plus size={10} weight="bold" /> Add part
              </button>
            )}
          </div>
        </td>
      </tr>
      {!isCollapsed && items.map((part, idx) => (
        <DataRow key={part.ID} part={part} onEdit={onEdit} rowIdx={idx} columns={columns} theme={theme} isACC={isACC} stickyLeftEdit={stickyLeftEdit} isDryer={isDryer} hpRange={hpRangeByPartId?.[part.ID]}
          isAdmin={isAdmin} showInListCol={showInListCol} onToggleInList={onToggleInList} onSaveField={onSaveField}
          onViewFiles={onViewFiles} onAddToCart={onAddToCart} onOpenPmSchedule={onOpenPmSchedule} pmTypes={pmTypes} showPmButton={showPmButton} stockByDisplayName={stockByDisplayName} actionsColWidth={actionsColWidth}
          bulkEditMode={bulkEditMode} bulkEdits={bulkEdits} onBulkEdit={onBulkEdit} />
      ))}
    </>
  );
}


function DownloadButton({ format, prefix, productClass, search, filters, userEmail, isACC, productType, productTypes, nameContains, stage }) {
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState("");

  const addEmail = () => {
    const e = emailInput.trim();
    if (e && e.includes("@") && !emails.includes(e)) {
      setEmails([...emails, e]);
      setEmailInput("");
    }
  };
  const removeEmail = (idx) => setEmails(emails.filter((_, i) => i !== idx));

  const handleDownload = async () => {
    setShowDialog(false);
    setLoading(true);
    try {
      const res = await exportPriceList({
        prefix, product_class: productClass, search, format,
        filters: filters || {}, requester_email: userEmail,
        notify_emails: emails,
        product_type: productType, product_types: productTypes, name_contains: nameContains,
        stage: stage || "",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `price_list_${prefix.toLowerCase()}.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download failed", e);
    } finally {
      setLoading(false);
      setEmails([]);
    }
  };

  const isPdf = format === "pdf";
  const accentCls = isACC
    ? "text-[#D80621] border-red-200 hover:bg-red-50"
    : "text-blue-700 border-blue-200 hover:bg-blue-50";

  return (
    <>
      <button
        data-testid={`download-${format}-button`}
        onClick={() => setShowDialog(true)}
        disabled={loading}
        className={`text-xs font-semibold px-2.5 py-1.5 rounded-sm flex items-center gap-1.5 border transition-colors ${accentCls} ${loading ? "opacity-70 pointer-events-none" : ""}`}
      >
        {loading ? (
          <Spinner size={14} className="animate-spin" />
        ) : (
          isPdf ? <FilePdf size={14} weight="bold" /> : <FileXls size={14} weight="bold" />
        )}
        {loading ? "Downloading..." : isPdf ? "PDF" : "Excel"}
      </button>

      {/* Email prompt dialog */}
      {showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/20" onClick={() => setShowDialog(false)} />
          <div className="relative w-full max-w-sm bg-white rounded-sm shadow-xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-sm font-bold font-['Chivo']">
                Download {isPdf ? "PDF" : "Excel"} — {prefix} Price List
              </h3>
              <button onClick={() => setShowDialog(false)} className="text-slate-400 hover:text-slate-600">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-500">
                Add email addresses to send this download to, or just download directly.
              </p>
              <div className="flex gap-1.5">
                <input
                  data-testid="download-email-input"
                  type="email"
                  placeholder="email@example.com"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                  className="flex-1 h-8 text-sm px-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  data-testid="download-email-add"
                  onClick={addEmail}
                  disabled={!emailInput.trim()}
                  className={`h-8 px-2 text-xs font-semibold rounded-sm border ${accentCls} disabled:opacity-30`}
                >
                  + Add
                </button>
              </div>

              {/* Email chips */}
              {emails.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {emails.map((em, idx) => (
                    <span key={em} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-sm border border-slate-200">
                      {em}
                      <button onClick={() => removeEmail(idx)} className="text-slate-400 hover:text-red-500">
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-slate-100">
                <button
                  data-testid="download-confirm-button"
                  onClick={handleDownload}
                  className={`flex-1 h-9 text-sm font-semibold rounded-sm text-white transition-colors ${
                    isACC ? "bg-[#D80621] hover:bg-[#b5051b]" : "bg-blue-700 hover:bg-blue-800"
                  }`}
                >
                  {emails.length > 0
                    ? `Download & Email to ${emails.length}`
                    : "Download Only"}
                </button>
                <button onClick={() => setShowDialog(false)}
                  className="h-9 px-3 text-sm text-slate-500 border border-slate-200 rounded-sm hover:bg-slate-50">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
